import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Users, Target, Calendar, Heart, DollarSign, Radio, Upload } from 'lucide-react';
import { motion } from 'framer-motion';
import { socialAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const ICON_MAP = {
  project_launch: Target,
  investment: DollarSign,
  event_created: Calendar,
  content_upload: Upload,
  follow: Users,
};

const COLOR_MAP = {
  project_launch: 'bg-emerald-100 text-emerald-600',
  investment: 'bg-green-100 text-green-600',
  event_created: 'bg-violet-100 text-violet-600',
  content_upload: 'bg-blue-100 text-blue-600',
  follow: 'bg-pink-100 text-pink-600',
};

function FeedItem({ item }) {
  const Icon = ICON_MAP[item.type] || Activity;
  const color = COLOR_MAP[item.type] || 'bg-zinc-100 text-zinc-600';

  const linkTo = item.target_type === 'project' ? `/projects/${item.target_id}`
    : item.target_type === 'event' ? `/events/${item.target_id}`
    : item.target_type === 'creator' ? `/creator/profile/${item.target_id}`
    : '#';

  return (
    <Link to={linkTo}>
      <div className="flex items-start gap-3 p-3 hover:bg-zinc-50 transition-colors rounded-xl">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-zinc-800"><span className="font-semibold">{item.user_name}</span> {item.title}</p>
          <p className="text-xs text-zinc-400 mt-0.5">{new Date(item.created_at).toLocaleString()}</p>
        </div>
      </div>
    </Link>
  );
}

export default function ActivityFeedPage() {
  const { user } = useAuth();
  const [myFeed, setMyFeed] = useState([]);
  const [globalFeed, setGlobalFeed] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [feedRes, globalRes, followRes] = await Promise.all([
          user ? socialAPI.feed() : Promise.resolve({ data: [] }),
          socialAPI.globalFeed(),
          user ? socialAPI.following() : Promise.resolve({ data: [] }),
        ]);
        setMyFeed(feedRes.data);
        setGlobalFeed(globalRes.data);
        setFollowing(followRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-8" data-testid="activity-feed-page">
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <h1 className="font-bold text-2xl text-zinc-900" data-testid="feed-title">Activity Feed</h1>
          <p className="text-zinc-500 text-sm mt-1">Stay updated on projects, events, and creators</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <Tabs defaultValue={user ? "following" : "global"}>
          <TabsList className="bg-white border border-zinc-200 p-1 mb-4">
            {user && <TabsTrigger value="following" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-sm">Following ({following.length})</TabsTrigger>}
            <TabsTrigger value="global" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-sm">Global</TabsTrigger>
          </TabsList>

          {user && (
            <TabsContent value="following">
              <div className="bg-white rounded-2xl border border-zinc-200">
                {myFeed.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="mx-auto mb-3 text-zinc-300" size={40} />
                    <p className="text-zinc-500 text-sm">Follow creators to see their activity here</p>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {myFeed.map((item, i) => <FeedItem key={item.id || i} item={item} />)}
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          <TabsContent value="global">
            <div className="bg-white rounded-2xl border border-zinc-200">
              {globalFeed.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="mx-auto mb-3 text-zinc-300" size={40} />
                  <p className="text-zinc-500 text-sm">No activity yet. Be the first to create something!</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {globalFeed.map((item, i) => <FeedItem key={item.id || i} item={item} />)}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
